import React, { useRef, lazy } from 'react';
import { motion, useInView } from 'framer-motion';
import { VideoIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
interface Interview {
  youtubeId: string;
  titleEn: string;
  titleEs: string;
}
const INTERVIEWS: Interview[] = [{
  youtubeId: 'ifWYG6li1DA',
  titleEn: 'HAA — Live',
  titleEs: 'HAA — En Vivo'
}, {
  youtubeId: 'dp99zNF9V6U',
  titleEn: 'HAA — Featured Conversation',
  titleEs: 'HAA — Conversación Destacada'
}, {
  youtubeId: '8Qxbu85SdpY',
  titleEn: 'HAA Interview — Live Conversation',
  titleEs: 'Entrevista HAA — Conversación en Vivo'
}];
export function InterviewsBanner() {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, {
    once: true,
    margin: '-60px'
  });
  const {
    language
  } = useLanguage();
  const isEs = language === 'ES';
  return <section className="relative bg-haa-navy py-16 lg:py-20 overflow-hidden" aria-label={isEs ? 'Entrevistas' : 'Interviews'} data-id="element-2537">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
      backgroundImage: 'radial-gradient(circle at 25% 50%, white 1px, transparent 1px)',
      backgroundSize: '40px 40px'
    }} data-id="element-2538" />

      <div ref={ref} className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2539">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 25
      }} animate={inView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} className="text-center mb-12" data-id="element-2540">
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/15 rounded-full px-4 py-2 mb-5 backdrop-blur-sm" data-id="element-2541">
            <VideoIcon className="w-4 h-4 text-haa-skyblue" data-id="element-2542" />
            <span className="font-nunito text-white/80 text-xs font-bold uppercase tracking-[0.2em]" data-id="element-2543">
              {isEs ? 'En los Medios' : 'In the Media'}
            </span>
          </div>
          <h2 className="font-bebas text-4xl sm:text-5xl lg:text-6xl text-white tracking-wider leading-none" data-id="element-2544">
            {isEs ? 'Entrevistas y Conversaciones' : 'Interviews & Conversations'}
          </h2>
          <div className="flex items-center justify-center gap-3 mt-4" data-id="element-2545">
            <div className="w-10 h-0.5 bg-haa-skyblue/60 rounded-full" data-id="element-2546" />
            <div className="w-2 h-2 rounded-full bg-haa-skyblue" data-id="element-2547" />
            <div className="w-10 h-0.5 bg-haa-midblue/60 rounded-full" data-id="element-2548" />
          </div>
          <p className="font-nunito text-white/50 text-base mt-5 max-w-xl mx-auto" data-id="element-2549">
            {isEs ? 'Conversaciones con líderes, medios y aliados que destacan la misión y el impacto de la Asociación Hondureño-Americana.' : 'Conversations with leaders, media, and partners spotlighting the mission and impact of the Honduran-American Association.'}
          </p>
        </motion.div>

        {/* Video Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-2550">
          {INTERVIEWS.map((interview, i) => <motion.div key={interview.youtubeId} initial={{
          opacity: 0,
          y: 30
        }} animate={inView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.15 + i * 0.12,
          ease: [0.16, 1, 0.3, 1]
        }} className="rounded-2xl overflow-hidden shadow-2xl shadow-black/40 bg-black/30 border border-white/10" data-id="element-2551">
              {/* Embedded YouTube Player */}
              <div className="relative w-full" style={{
            paddingBottom: '56.25%'
          }} data-id="element-2552">
                <iframe src={`https://www.youtube-nocookie.com/embed/${interview.youtubeId}?rel=0`} title={isEs ? interview.titleEs : interview.titleEn} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen className="absolute inset-0 w-full h-full" loading="lazy" data-id="element-2553" />
              </div>

              {/* Label */}
              <div className="px-5 py-4 bg-gradient-to-r from-haa-navy to-blue-950" data-id="element-2554">
                <p className="font-nunito text-white/90 text-sm font-semibold" data-id="element-2555">
                  {isEs ? interview.titleEs : interview.titleEn}
                </p>
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
}